import type { JobReasonCodeInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import {
  createJobReasonCode,
  deactivateJobReasonCode,
  listJobReasonCodes,
  reactivateJobReasonCode,
  updateJobReasonCode,
  type JobReasonCode,
} from '../../../reference-data.api';

export type JobReasonCodesState = { status: 'loading' } | { status: 'loaded'; codes: JobReasonCode[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): JobReasonCodesState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

function toSaveError(err: unknown): string {
  return err instanceof ApiError && err.status === 409 ? 'A reason code with this code already exists.' : 'Could not save — check the fields and try again.';
}

/** Same shape as useRequiredDocumentTypes: create/update keep their error inline, deactivate/reactivate toast. */
export function useJobReasonCodes() {
  const [state, setState] = useState<JobReasonCodesState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    listJobReasonCodes()
      .then((codes) => {
        if (!cancelled) setState({ status: 'loaded', codes });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-fetches after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', codes: await listJobReasonCodes() });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  async function save(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch (err) {
      setSaveError(toSaveError(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  const create = (input: JobReasonCodeInput) => save(() => createJobReasonCode(input), `${input.name} created`);
  const update = (id: string, input: JobReasonCodeInput) => save(() => updateJobReasonCode(id, input), `${input.name} updated`);
  const deactivate = (row: JobReasonCode) => rowAction(() => deactivateJobReasonCode(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`);
  const reactivate = (row: JobReasonCode) => rowAction(() => reactivateJobReasonCode(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`);

  return { state, saveError, saving, create, update, resetSaveError: () => setSaveError(null), deactivate, reactivate };
}
